import { Icons, Stack, Tooltip } from '@healthinal/ui';
import { useNavigate } from '@tanstack/react-router';
import { useAtom } from 'jotai';
import { RefObject } from 'react';
import type {
  PatientSummaryTracingContactDto,
  PatientSummaryWithTracingContactsDto,
} from '../../../../api/created/patientSummary.ts';
import {
  PatientSummaryDto,
  PatientSummaryDtoContactTracing,
  PatientSummaryDtoContactTracingStatus,
  TracingContactDetailsDtoAssessmentStatus,
  TracingContactStatusDetailsDto,
} from '../../../../api/generated/model';
import { TooltipCell } from '../../../../components/table/TooltipCell.tsx';
import { VirtualTablePaddingRow } from '../../../../components/table/VirtualTablePaddingRow.tsx';
import { useCustomVirtualizer } from '../../../../hooks/useCustomVirtualizer.ts';
import { tracingContactSelectedPatientFilterAtom } from '../../../../state/tracingContactSearchAtoms.ts';
import { PATIENT_TABLE_CELL_HEIGHT } from '../../../../theme.ts';
import { patientSummaryDtoToOutlinedPatientDto } from '../../../../utils/patient.ts';
import { PatientTable } from './PatientTable.tsx';
import { PatientTablePatientInfosData, PatientTablePatientInfosHeader } from './PatientTablePatientInfos.tsx';

export function PatientsContactTracingTable({
  patients,
  parentRef,
}: {
  patients: PatientSummaryWithTracingContactsDto[];
  parentRef: RefObject<HTMLElement | null>;
}) {
  const navigate = useNavigate();

  const [_, setSelectedPatient] = useAtom(tracingContactSelectedPatientFilterAtom);

  const { virtualItems, totalSize, paddingTop, paddingBottom } = useCustomVirtualizer({
    count: patients.length,
    cellHeight: PATIENT_TABLE_CELL_HEIGHT,
    parentRef: parentRef,
  });

  const handleRowClick = (patient: PatientSummaryDto) => {
    setSelectedPatient(patientSummaryDtoToOutlinedPatientDto(patient));
    void navigate({ to: '/contact-tracing' });
  };

  return (
    <PatientTable stickyHeader borderAxis="xBetween" noWrap hoverRow>
      <thead>
        <tr>
          <th style={{ width: 130 }}>Fallnummer</th>
          <PatientTablePatientInfosHeader showCreatedAt={false} />
          <th style={{ width: 100 }}>Status</th>
          <th>Kontakte</th>
          <th>Einschätzung nötig</th>
          <th>Getestet</th>
          <th>Positiv</th>
          <th>Negativ</th>
        </tr>
      </thead>
      <tbody style={{ height: totalSize }}>
        <VirtualTablePaddingRow height={paddingTop} />

        {virtualItems.map((virtualRow) => {
          const patient = patients[virtualRow.index];
          const contactsCount = getContactsCount(patient.contactTracing, patient.tracingContacts);
          return (
            <tr key={virtualRow.index} onClick={() => handleRowClick(patient)}>
              <TooltipCell>{patient.caseNumber}</TooltipCell>
              <PatientTablePatientInfosData patient={patient} showCreatedAt={false} />
              <td>{getContactTracingStatusIcon(patient.contactTracing, patient.contactTracingStatus)}</td>
              <td>{contactsCount}</td>
              <td>
                {getContactCountForAssessmentStatus(
                  contactsCount,
                  patient.tracingContacts,
                  TracingContactDetailsDtoAssessmentStatus.PENDING,
                )}
              </td>
              <td>{getContactCountForFlag(contactsCount, patient.tracingContacts, 'tested')}</td>
              <td>{getContactCountForFlag(contactsCount, patient.tracingContacts, 'positive')}</td>
              <td>{getContactCountForFlag(contactsCount, patient.tracingContacts, 'negative')}</td>
            </tr>
          );
        })}

        <VirtualTablePaddingRow height={paddingBottom} />
      </tbody>
    </PatientTable>
  );
}

function getContactTracingStatusIcon(
  contactTracing: PatientSummaryDtoContactTracing,
  status: PatientSummaryDtoContactTracingStatus,
) {
  switch (contactTracing) {
    case PatientSummaryDtoContactTracing.NO:
      return (
        <Tooltip title={'Kein Contact Tracing nötig'}>
          <Stack alignContent={'center'}>
            <Icons.RemoveCircleOutline size={'sm'} color={'success'} />
          </Stack>
        </Tooltip>
      );
    case PatientSummaryDtoContactTracing.UNKNOWN:
      return (
        <Tooltip title={'Keine Einschätzung vorhanden'}>
          <Stack alignContent={'center'}>
            <Icons.HelpOutline size={'sm'} />
          </Stack>
        </Tooltip>
      );
    case PatientSummaryDtoContactTracing.YES:
      switch (status) {
        case PatientSummaryDtoContactTracingStatus.COMPLETED:
          return (
            <Tooltip title={'Contact Tracing abgeschlossen'}>
              <Stack alignContent={'center'}>
                <Icons.CheckCircleOutlined color={'success'} size={'sm'} />
              </Stack>
            </Tooltip>
          );
        case PatientSummaryDtoContactTracingStatus.UNKNOWN:
          return (
            <Tooltip title={'Contact Tracing notwendig, aber noch nicht gestartet'}>
              <Stack alignContent={'center'}>
                <Icons.ErrorOutline color={'warning'} size={'sm'} />
              </Stack>
            </Tooltip>
          );
        case PatientSummaryDtoContactTracingStatus.NOT_COMPLETED:
          return (
            <Tooltip title={'In Arbeit'}>
              <Stack alignContent={'center'}>
                <Icons.ChangeCircleOutlined color={'warning'} size={'sm'} />
              </Stack>
            </Tooltip>
          );
      }
  }
}

function getContactsCount(contactTracing: PatientSummaryDtoContactTracing, contacts: TracingContactStatusDetailsDto[]) {
  if (contacts.length > 0) {
    return contacts.length;
  }
  if (contactTracing !== PatientSummaryDtoContactTracing.YES) {
    return undefined;
  }
  return 0;
}

function getContactCountForFlag(
  contactsCount: number | undefined,
  contacts: PatientSummaryTracingContactDto[],
  flag: 'tested' | 'positive' | 'negative',
) {
  if (contactsCount === undefined) {
    return undefined;
  }
  const matchingContacts = contacts.filter((contact) => contact[flag] === true);

  return `${matchingContacts.length} / ${contactsCount}`;
}

function getContactCountForAssessmentStatus(
  contactsCount: number | undefined,
  contacts: TracingContactStatusDetailsDto[],
  status: TracingContactDetailsDtoAssessmentStatus,
) {
  if (contactsCount === undefined) {
    return undefined;
  }
  const contactsInStatus = contacts.filter((contact) => contact.assessmentStatus === status);

  return `${contactsInStatus.length} / ${contactsCount}`;
}
